/**
 * Global light/dark theme-mode context.
 *
 * Net-new: this app previously had a single static MUI theme with no dark
 * mode at all (see App.tsx history). This context holds only the `mode`
 * string ("light" | "dark") and a toggle, persisted to localStorage so the
 * choice survives refreshes/new sessions. The actual MUI `Theme` object is
 * built from this mode in App.tsx (`AppThemeProvider`) — kept separate so
 * this context has zero MUI-theme-shape coupling.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "tekinvest_theme_mode";

export interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
}

export const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

function readStoredMode(): ThemeMode {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Storage unavailable (private browsing, quota, etc.) — toggle still
      // works for the current page, it just won't persist across reloads.
    }
  }, [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const value = useMemo<ThemeModeContextValue>(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export default ThemeModeContext;
